import React from 'react';
import PropTypes from 'prop-types';
import { ArrowUpRight } from '@phosphor-icons/react';
import { useShowcaseText } from '../../i18n/useShowcaseText';
import { config } from '../../config/runtimeConfig';
import './ClassifiedsSync.css';

/**
 * Площадки, на которых стоит то же объявление.
 *
 * Список приходит пропом — у Кипра и Люксембурга он разный, и Антон отдельно
 * отметил на синке #237, что «Базараки только кипрские».
 *
 * ⚠️ Дефолтные `url` ведут на ПОИСК площадки, а не на конкретное объявление:
 * интеграции ещё нет, публиковать нечего и ссылки на объявление не существует.
 * Когда автопубликация появится, бэкенд отдаст на каждый канал ссылку прямо на
 * листинг, и подменится только этот массив — разметка не меняется.
 */
const DEFAULT_CHANNELS = [
  {
    name: 'Bazaraki',
    mark: 'B',
    color: '#f36f21',
    ink: '#ffffff',
    logo: '/assets/channels/bazaraki.svg',
    url: 'https://www.bazaraki.com/car-motorbikes-boats-and-parts/cars-trucks-and-vans/',
  },
  {
    name: 'AutoScout24',
    mark: 'A',
    color: '#f5a623',
    ink: '#0e0f10',
    logo: '/assets/channels/autoscout24.svg',
    url: 'https://www.autoscout24.com/lst',
  },
  {
    name: 'mobile.de',
    mark: 'm',
    color: '#0a7cff',
    ink: '#ffffff',
    logo: '/assets/channels/mobile-de.svg',
    url: 'https://suchen.mobile.de/fahrzeuge/search.html',
  },
];

/**
 * ClassifiedsSync — «эта же машина на площадках» на карточке авто
 * (#1338, план #1330).
 *
 * ## Подача — ряд значков рядом с кнопками
 *
 * Раньше блок был списком строк со статусом синхронизации у каждой. Автор
 * витрины попросил свести его к значкам площадок рядом с кнопками действия:
 * на карточке машины это не отдельный раздел, а такой же переход, как
 * «позвонить» или «спросить».
 *
 * Статус («Synced today») остался подписью ко всему ряду, а не к каждой
 * площадке: одна строка вместо трёх одинаковых, и это правда — синхронизация
 * общая.
 *
 * ## Про логотипы
 *
 * Компонент показывает НАСТОЯЩИЙ знак площадки, если файл лежит по адресу из
 * `logo` (`public/assets/channels/<канал>.svg`). Файла нет — картинка сама
 * себя снимает со страницы (`onError`), и остаётся запасной вариант: название
 * площадки её фирменным цветом.
 *
 * Файлов в репозитории пока нет намеренно. Логотип площадки — чужой товарный
 * знак: его берут из официального бренд-пака владельца и по его правилам, а
 * не перерисовывают и не выкачивают со страницы. Положить файл в
 * `public/assets/channels/` достаточно — разметка и стили уже готовы.
 *
 * Запасной вариант — НАЗВАНИЕ. На витрине перед ним стоит первая буква
 * площадки в кружке её фирменного цвета (`mark`, решение автора витрины,
 * #1335): пока настоящих знаков нет, ряд различается по цвету площадок. Фирменный цвет в нём декоративный (подчёркивание), а сам текст
 * идёт цветом страницы: у оранжевого Bazaraki на белом 3.4:1, у жёлтого
 * AutoScout24 — 1.9:1, то есть названием их набирать нельзя.
 *
 * ⚠️ Блок демо-онли по флагу. Файл лежит в `pages/shared/`, но вставлен в
 * `CarDetailPage`, а та ЕДЕТ ДИЛЕРАМ через `components/registry/Catalog.jsx`.
 * Поэтому рендер закрыт `config.SHOW_CLASSIFIEDS_SYNC` с дефолтом `false`.
 */
export const ClassifiedsSync = ({
  channels = DEFAULT_CHANNELS,
  status,
}) => {
  const showcaseText = useShowcaseText();
  const statusText = status ?? showcaseText('showcase.car.syncedToday', 'Synced today');
  const newTab = showcaseText('showcase.a11y.newTab', 'opens in a new tab');
  // Какие логотипы не загрузились. Ключ — имя канала: путь может быть и
  // одинаковым у двух каналов, имя — нет.
  const [missingLogos, setMissingLogos] = React.useState({});

  if (!channels?.length) return null;

  return (
    <div className="classifieds-sync">
      <ul className="classifieds-sync__list">
        {channels.map(({ name, mark, color, ink, logo, url }) => (
          <li key={name}>
            <a
              className="classifieds-sync__link"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              /* Название площадки — доступное имя ссылки: сам знак читалке
                 ничего не говорит, поэтому он `alt=""`. */
              aria-label={`${name} — ${newTab}`}
              title={name}
            >
              {/* Витрина (#1335): первая буква площадки в кружке её
                  фирменного цвета — до настоящих знаков из бренд-паков.
                  Декор: имя ссылки даёт `aria-label`. Цвет буквы задан
                  парой к фону: на жёлтом AutoScout24 белая не читается. */}
              {config.IS_OWN_STOCK && mark && (
                <span
                  className="classifieds-sync__mark"
                  style={{
                    '--classified-color': color,
                    '--classified-ink': ink,
                  }}
                  aria-hidden="true"
                >
                  {mark}
                </span>
              )}
              {logo && !missingLogos[name] ? (
                <img
                  className="classifieds-sync__logo"
                  src={logo}
                  alt=""
                  loading="lazy"
                  /* Файла нет — снимаем картинку и показываем название.
                     Без этого браузер рисует сломанный значок, а он хуже
                     любой заглушки. */
                  onError={() =>
                    setMissingLogos((seen) => ({ ...seen, [name]: true }))
                  }
                />
              ) : (
                <span
                  className="classifieds-sync__name"
                  style={{ '--classified-color': color }}
                >
                  {name}
                </span>
              )}
              <ArrowUpRight size={14} weight="bold" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>

      {statusText && <p className="classifieds-sync__status">{statusText}</p>}
    </div>
  );
};

ClassifiedsSync.propTypes = {
  /**
   * Площадки. `logo` — путь к официальному знаку в `public/`; нет файла —
   * показываем название, подчёркнутое фирменным цветом `color`.
   */
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      /** Буква знака-заглушки на витрине и её цвет на фоне `color`. */
      mark: PropTypes.string,
      ink: PropTypes.string,
      color: PropTypes.string,
      logo: PropTypes.string,
      url: PropTypes.string.isRequired,
    })
  ),
  /** Подпись под рядом: синхронизация общая, поэтому она одна на все площадки. */
  status: PropTypes.string,
};

export default ClassifiedsSync;
